import { FC, useEffect } from 'react';
import { Modal, Form, Row, Col, Input, Select, Divider, Checkbox } from 'antd';
import { useApp } from '@/App';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
	getAllUsers,
	getAllRoles,
	updateUser,
	AdminUserData,
	AdminDivisionData,
} from '@/libs/fleet-api/services/admin';
import { useAsyncModal } from '@/libs/antd/components/Modal';
import { useForm } from '@/libs/antd/form';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import formRule from '@/libs/antd/rules';

export const useEditUserModal = () => {
	return useAsyncModal<{ user: AdminUserData }, void>();
};

export type EditUserModalProps = {
	modal: ReturnType<typeof useEditUserModal>['modal'] | null;
	data: ReturnType<typeof useEditUserModal>['data'];
	divisions?: AdminDivisionData[];
	onClose: () => void;
};

const EditUserModal: FC<EditUserModalProps> = ({
	modal,
	data,
	divisions = [],
	onClose,
}) => {
	const { message } = useApp();
	const queryClient = useQueryClient();
	const { form } = useForm();

	const { data: roles } = useQuery({
		queryKey: ['admin-roles'],
		queryFn: getAllRoles,
	});

	const { data: users } = useQuery({
		queryKey: ['admin-users'],
		queryFn: getAllUsers,
	});

	const user = data?.user;

	const updateUserMutation = useMutation({
		mutationFn: updateUser,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['admin-users'] });
			message.success('Pengguna berhasil diperbarui');
			onClose();
		},
		onError: (error: unknown) => {
			message.error('Gagal memperbarui pengguna');
			console.error(error);
		},
	});

	const handleSubmit = async (values: unknown) => {
		if (!user) return;

		updateUserMutation.mutate({
			userId: user.userId,
			...(values as Record<string, unknown>),
		});
	};

	useEffect(() => {
		if (user && form) {
			form.setFieldsValue({
				username: user.username,
				fullName: user.fullName,
				email: user.email,
				groupId: user.group.id,
				divisionId: user.division?.id,
				reportToId: user.reportToId,
				isExternalUser: user.isExternalUser,
			});
		}
	}, [user, form]);

	if (!modal?.open || !user) return null;

	const availableManagers =
		users?.data?.filter(
			(u) =>
				u.isActive &&
				u.userId !== user?.userId &&
				u.group.code !== 'FRM' &&
				u.group.code !== 'CCA',
		) || [];

	return (
		<Modal
			{...modal}
			title={`Edit Pengguna: ${user.fullName}`}
			okText="Save"
			confirmLoading={updateUserMutation.isPending}
			onOk={() => form.submit()}
			width={600}
		>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				layout="horizontal"
				onFinish={handleSubmit}
			>
				<Row gutter={[40, 20]}>
					<Col span={24}>
						<Form.Item
							name="username"
							label="Username"
							rules={[
								formRule.required('Username wajib diisi'),
								{
									validator: (_, value) => {
										if (
											value &&
											users?.data?.some(
												(u) =>
													u.username.toLowerCase() === value.toLowerCase() &&
													u.userId !== user?.userId,
											)
										) {
											return Promise.reject('Username sudah digunakan');
										}
										return Promise.resolve();
									},
								},
							]}
						>
							<Input placeholder="Masukkan username unik" />
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item
							name="fullName"
							label="Nama Lengkap"
							rules={[formRule.required('Nama lengkap wajib diisi')]}
						>
							<Input placeholder="Masukkan nama lengkap" />
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item
							name="email"
							label="Email"
							rules={[
								formRule.required('Email wajib diisi'),
								formRule.email('Format email tidak valid'),
								{
									validator: (_, value) => {
										if (
											value &&
											users?.data?.some(
												(u) =>
													u.email.toLowerCase() === value.toLowerCase() &&
													u.userId !== user?.userId,
											)
										) {
											return Promise.reject('Email sudah digunakan');
										}
										return Promise.resolve();
									},
								},
							]}
						>
							<Input placeholder="Masukkan alamat email" />
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item
							name="groupId"
							label="Role"
							rules={[formRule.required('Role wajib dipilih')]}
						>
							<Select
								placeholder="Pilih role pengguna"
								options={roles?.data?.map((role) => ({
									label: role.name,
									value: role.id,
								}))}
							/>
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item
							name="divisionId"
							label="Divisi"
						>
							<Select
								placeholder="Pilih divisi (opsional)"
								allowClear
								options={divisions?.map((division) => ({
									label: division.name,
									value: division.id,
								}))}
							/>
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item
							name="reportToId"
							label="Atasan"
						>
							<Select
								placeholder="Pilih atasan (opsional)"
								allowClear
								options={availableManagers?.map((manager) => ({
									label: `${manager.fullName} (${manager.group.name})`,
									value: manager.userId,
								}))}
							/>
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item
							name="isExternalUser"
							label="Pengguna Eksternal"
							valuePropName="checked"
						>
							<Checkbox>User ESTAR</Checkbox>
						</Form.Item>
					</Col>
				</Row>
				<Divider style={{ margin: '40px 0' }} />
			</Form>
		</Modal>
	);
};

export default EditUserModal;
